import { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'universal-scripts';

interface ProcessMessage {
	type: 'process_active_query' | 'process_active_response' | 'process_completed' | 'heartbeat';
	processId: string;
	videoUrl: string;
	tabId: string;
}

export function useProcessCoordination() {
	const videoUrl = useAppSelector((state) => state.sentinel.videoUrl);
	const processId = useAppSelector((state) => state.sentinel.processId);
	const status = useAppSelector((state) => state.sentinel.status);
	const [canStartProcess, setCanStartProcess] = useState<boolean | null>(null);
	const [otherTabVideoUrl, setOtherTabVideoUrl] = useState<string | null>(null);
	const channelRef = useRef<BroadcastChannel | null>(null);
	const tabIdRef = useRef<string>(`tab-${Date.now()}-${Math.random()}`);
	const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const processIdRef = useRef(processId);
	const videoUrlRef = useRef(videoUrl);
	const statusRef = useRef(status);
	const isProcessingRef = useRef(false);
	useEffect(() => {
		processIdRef.current = processId;
		videoUrlRef.current = videoUrl;
		statusRef.current = status;
		isProcessingRef.current = status === 'processing';
	}, [processId, videoUrl, status]);
	useEffect(() => {
		const channel = new BroadcastChannel('sentinel-process');
		channelRef.current = channel;

		channel.onmessage = (event: MessageEvent<ProcessMessage>) => {
			const message = event.data;
			if (message.tabId === tabIdRef.current) return;
			if (message.type === 'process_active_query') {
				if (isProcessingRef.current && processIdRef.current && videoUrlRef.current) {
					channel.postMessage({
						type: 'process_active_response',
						processId: processIdRef.current,
						videoUrl: videoUrlRef.current,
						tabId: tabIdRef.current
					});
				}
			}
			if (message.type === 'process_active_response') {
				setCanStartProcess(false);
				setOtherTabVideoUrl(message.videoUrl);
			}
			if (message.type === 'process_completed') {
				setCanStartProcess(true);
				setOtherTabVideoUrl(null);
			}
		};

		return () => {
			channel.close();
			if (heartbeatIntervalRef.current) {
				clearInterval(heartbeatIntervalRef.current);
			}
		};
	}, []);
	useEffect(() => {
		if (!processId || !videoUrl) {
			setCanStartProcess(null);
			return;
		}

		const channel = channelRef.current;
		if (!channel) return;
		setCanStartProcess(null);
		channel.postMessage({
			type: 'process_active_query',
			processId,
			videoUrl,
			tabId: tabIdRef.current
		});

		const queryTimeout = setTimeout(() => {
			setCanStartProcess((prev) => (prev === null ? true : prev));
		}, 200);

		const stopHeartbeat = () => {
			if (heartbeatIntervalRef.current) {
				clearInterval(heartbeatIntervalRef.current);
				heartbeatIntervalRef.current = null;
			}
		};

		stopHeartbeat();

		if (status === 'processing') {
			heartbeatIntervalRef.current = setInterval(() => {
				channel.postMessage({
					type: 'heartbeat',
					processId,
					videoUrl,
					tabId: tabIdRef.current
				});
			}, 2000);
		} else if (status === 'completed' || status === 'error') {
			channel.postMessage({
				type: 'process_completed',
				processId,
				videoUrl,
				tabId: tabIdRef.current
			});
		}

		return () => {
			clearTimeout(queryTimeout);
			stopHeartbeat();
		};
	}, [processId, videoUrl, status]);

	return {
		canStartProcess,
		otherTabVideoUrl
	};
}
